import Iconify from 'src/components/Iconify';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { Switch } from 'src/@/components/ui/switch';

const DIAS_SEMANA = [
    { key: 'seg', label: 'Segunda-feira' },
    { key: 'ter', label: 'Terça-feira' },
    { key: 'qua', label: 'Quarta-feira' },
    { key: 'qui', label: 'Quinta-feira' },
    { key: 'sex', label: 'Sexta-feira' },
    { key: 'sab', label: 'Sábado' },
    { key: 'dom', label: 'Domingo' },
];

const DEFAULT_HORARIOS = {
    seg: { ativo: true, de: '09:00', ate: '17:00' },
    ter: { ativo: true, de: '09:00', ate: '17:00' },
    qua: { ativo: true, de: '09:00', ate: '17:00' },
    qui: { ativo: true, de: '09:00', ate: '17:00' },
    sex: { ativo: true, de: '09:00', ate: '17:00' },
    sab: { ativo: false, de: '09:00', ate: '13:00' },
    dom: { ativo: false, de: '09:00', ate: '13:00' },
};

const TEMPO_REENGAJAMENTO = ['1h', '2h', '3h', '4h', '5h', '6h'];

const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

export default function FuncoesSection({ aiAgent, changeModuleConfig }) {
    const funcoes = aiAgent?.config?.module?.realstate?.funcoes || {};
    const visita = funcoes.solicitacaoVisita || {};
    const endereco = funcoes.envioEnderecoCompleto || {};
    const reengajamento = funcoes.reengajamento || {};
    const horarios = visita.horarios || DEFAULT_HORARIOS;

    const updateFuncoes = (patch) => {
        changeModuleConfig({
            funcoes: {
                ...funcoes,
                ...patch,
            },
        });
    };

    const updateVisita = (patch) => {
        updateFuncoes({ solicitacaoVisita: { ...visita, ...patch } });
    };

    const updateHorario = (dia, patch) => {
        updateVisita({
            horarios: {
                ...horarios,
                [dia]: { ...(horarios[dia] || DEFAULT_HORARIOS[dia]), ...patch },
            },
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <Card className="rounded-xl border border-gray-100 bg-white">
                <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                                <Iconify icon="mdi:calendar-clock" width={20} className="text-violet-600" />
                            </div>
                            <div>
                                <CardTitle>Solicitação de Visita</CardTitle>
                                <CardDescription>Permite que o cliente solicite agendamento de visita.</CardDescription>
                            </div>
                        </div>
                        <Switch
                            checked={!!visita.ativo}
                            onCheckedChange={(value) => updateVisita({ ativo: value })}
                        />
                    </div>
                </CardHeader>
                {visita.ativo && (
                    <CardContent className="flex flex-col gap-4">
                        <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                            <AlertDescription className="text-xs">
                                A Lais não confirma automaticamente o agendamento — ela informa ao cliente que o corretor ainda precisa validar a data.
                            </AlertDescription>
                        </Alert>

                        <Separator className="bg-gray-200/60" />

                        <div className="flex flex-col gap-2">
                            <h4 className="text-sm font-semibold text-gray-900">Horários de visita</h4>
                            <p className="text-xs text-gray-500">Habilite os dias e defina o intervalo em que visitas podem ser solicitadas.</p>
                        </div>

                        <div className="flex flex-col gap-2">
                            {DIAS_SEMANA.map(({ key, label }) => {
                                const h = horarios[key] || DEFAULT_HORARIOS[key];
                                return (
                                    <div
                                        key={key}
                                        className="grid grid-cols-[140px_48px_1fr_auto_1fr] items-center gap-3 py-2 px-3 rounded-xl bg-gray-50/60"
                                    >
                                        <span className="text-sm font-medium text-gray-700">{label}</span>
                                        <Switch
                                            checked={!!h.ativo}
                                            onCheckedChange={(value) => updateHorario(key, { ativo: value })}
                                        />
                                        <Input
                                            type="time"
                                            value={h.de || '09:00'}
                                            disabled={!h.ativo}
                                            onChange={(e) => updateHorario(key, { de: e.target.value })}
                                            className={`${INPUT_CLS} ${!h.ativo ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                                        />
                                        <span className="text-xs text-gray-400">até</span>
                                        <Input
                                            type="time"
                                            value={h.ate || '17:00'}
                                            disabled={!h.ativo}
                                            onChange={(e) => updateHorario(key, { ate: e.target.value })}
                                            className={`${INPUT_CLS} ${!h.ativo ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                )}
            </Card>

            <Card className="rounded-xl border border-gray-100 bg-white">
                <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50">
                                <Iconify icon="mdi:map-marker-outline" width={20} className="text-blue-600" />
                            </div>
                            <div>
                                <CardTitle>Envio de Endereço Completo</CardTitle>
                                <CardDescription>Autoriza a Lais a informar o endereço completo dos imóveis.</CardDescription>
                            </div>
                        </div>
                        <Switch
                            checked={!!endereco.ativo}
                            onCheckedChange={(value) => updateFuncoes({ envioEnderecoCompleto: { ...endereco, ativo: value } })}
                        />
                    </div>
                </CardHeader>
                {endereco.ativo && (
                    <CardContent>
                        <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                            <AlertDescription className="text-xs">
                                Garanta que todos os imóveis da Prateleira de Anúncios tenham endereço completo cadastrado (rua, bairro e cidade).
                            </AlertDescription>
                        </Alert>
                    </CardContent>
                )}
            </Card>

            <Card className="rounded-xl border border-gray-100 bg-white">
                <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-50">
                                <Iconify icon="mdi:message-reply-outline" width={20} className="text-green-600" />
                            </div>
                            <div>
                                <CardTitle>Reengajamento</CardTitle>
                                <CardDescription>Retoma conversas inativas após um intervalo definido.</CardDescription>
                            </div>
                        </div>
                        <Switch
                            checked={!!reengajamento.ativo}
                            onCheckedChange={(value) => updateFuncoes({ reengajamento: { ...reengajamento, ativo: value } })}
                        />
                    </div>
                </CardHeader>
                {reengajamento.ativo && (
                    <CardContent className="flex flex-col gap-3">
                        <div className="flex flex-col gap-2 max-w-xs">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Tempo para novo contato</Label>
                            <Select
                                value={reengajamento.tempoNovoContato || '6h'}
                                onValueChange={(value) => updateFuncoes({ reengajamento: { ...reengajamento, tempoNovoContato: value } })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TEMPO_REENGAJAMENTO.map((t) => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <p className="text-xs text-gray-500">A Lais tentará o reengajamento apenas uma vez, entre 8h e 20h.</p>
                    </CardContent>
                )}
            </Card>
        </div>
    );
}
